import { memo } from 'react';
import { Stack } from '@chakra-ui/react';

import { AddAdditionalContentButton } from '../buttons/AddAdditionalContentButton.component';
import { ContentBlock } from '../ContentBlock.component';
import { useHasManyHandler } from './hooks/useHasManyHandler';
import { updateLastDocumentPathElementWithIdSuffix } from './logic/updateLastDocumentPathElementWithIdSuffix';
import { HasManyHandlerProps } from './types/documentDataViewHandlerProps';
import { AdditionalValuesCard } from './AdditionalValuesCard';
import { ExistingValuesCard } from './ExistingValuesCard';

const HasManyHandlerComponent = <DocumentDefinition,>({
  children,
  additionalValues,
  documentPath,
  documentTarget,
  existingValues,
}: HasManyHandlerProps<DocumentDefinition>) => {
  const {
    existingValuesFieldArray,
    additionalValuesFieldArray,
    additionalValuesDefaultValue,
  } = useHasManyHandler<DocumentDefinition>(documentPath, additionalValues);

  return (
    <>
      <Stack gap={12}>
        {documentTarget.length > 0 && (
          <>
            <ContentBlock {...existingValues.ContentBlockProps} />

            <Stack gap={6}>
              {documentTarget.map((document, idx) => (
                <ExistingValuesCard
                  key={document.id}
                  existingValues={existingValues}
                  namePrefixes={['existingValues', idx]}
                  parentContext={document}
                  workflowDataResolverDependencies={{
                    documentPath: updateLastDocumentPathElementWithIdSuffix(
                      documentPath,
                      document.id,
                    ),
                  }}
                />
              ))}
            </Stack>
          </>
        )}

        {additionalValues && (
          <>
            {additionalValuesFieldArray.fields.length > 0 && (
              <>
                <ContentBlock {...additionalValues.ContentBlockProps} />

                <Stack gap={6}>
                  {additionalValuesFieldArray.fields.map((value, idx) => (
                    <AdditionalValuesCard
                      key={value.id}
                      formBlocks={additionalValues.formBlocks}
                      namePrefixes={['additionalValues', idx]}
                      onRemove={() => additionalValuesFieldArray.remove(idx)}
                    />
                  ))}
                </Stack>
              </>
            )}

            <AddAdditionalContentButton
              onClick={() =>
                additionalValuesFieldArray.append(additionalValuesDefaultValue)
              }
            >
              {additionalValues.addAdditionalContentButtonText}
            </AddAdditionalContentButton>
          </>
        )}
      </Stack>
      {children}
    </>
  );
};

export const HasManyHandler = memo(HasManyHandlerComponent);
